import React, { useEffect, useState } from 'react';
import { submitActionCardFlag } from '../services/api';

const REASONS = [
  { value: 'video', label: "The video doesn't match this skill" },
  { value: 'activity', label: "The activity or resource doesn't match" },
  { value: 'learning_outcome', label: "The learning outcome doesn't match" },
  { value: 'other', label: 'Something else' },
];

function FlagIcon({ filled = false }: any) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M5 21V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M5 4h11l-2 4 2 4H5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill={filled ? 'currentColor' : 'none'} />
    </svg>
  );
}

export default function ActionCardFlag({ studentId, standardCode, subject, grade, domain, strand }: any) {
  const [open, setOpen] = useState<any>(false);
  const [reason, setReason] = useState('video');
  const [details, setDetails] = useState<any>('');
  const [saving, setSaving] = useState<any>(false);
  const [error, setError] = useState<any>('');
  const [sent, setSent] = useState<any>(false);
  const [showThanks, setShowThanks] = useState<any>(false);

  useEffect(() => {
    if (!showThanks) return undefined;
    const timer = setTimeout(() => setShowThanks(false), 4000);
    return () => clearTimeout(timer);
  }, [showThanks]);

  if (!studentId || !standardCode) return null;

  const close = () => {
    setOpen(false);
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      await submitActionCardFlag(studentId, {
        standard_code: String(standardCode),
        reason,
        details: details.trim() || null,
        subject: subject ? String(subject) : null,
        grade: grade !== undefined && grade !== null && grade !== '' ? String(grade) : null,
        domain: domain ? String(domain) : null,
        strand: strand ? String(strand) : null,
      });
      setSent(true);
      setShowThanks(true);
      setOpen(false);
      setDetails('');
    } catch (err: any) {
      setError(err.message || 'Could not send your flag. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'relative', flex: '0 0 auto' }}>
      <button
        type="button"
        onClick={() => { if (open) close(); else { setShowThanks(false); setOpen(true); } }}
        aria-expanded={open}
        aria-label={sent ? 'Resource flagged. Flag another issue with this Action Card' : 'Flag a resource that does not match this Action Card'}
        title={sent ? 'Thanks — flagged for review' : "Flag a resource that doesn't match"}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30,
          border: `1px solid ${sent ? '#f59e0b' : '#b8cae5'}`, borderRadius: 999,
          background: sent ? '#fef3c7' : '#fff', color: sent ? '#b45309' : '#64748b', cursor: 'pointer',
        }}
      >
        <FlagIcon filled={sent} />
      </button>
      {open && (
        <form
          onSubmit={submit}
          aria-label="Flag this Action Card"
          style={{
            position: 'absolute', right: 0, top: 36, zIndex: 20, width: 280, padding: 12,
            background: '#fff', border: '1px solid #b8cae5', borderRadius: 10,
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.16)', textAlign: 'left',
          }}
        >
          <div style={{ color: '#1e3a5f', fontSize: 13, fontWeight: 800, marginBottom: 4 }}>Flag this Action Card</div>
          <div style={{ color: '#475569', fontSize: 11, lineHeight: 1.4, marginBottom: 8 }}>
            Let us know if a resource doesn't match this skill. Our team will review it.
          </div>
          <label htmlFor={`flag-reason-${standardCode}`} style={{ display: 'block', color: '#334155', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>What doesn't match?</label>
          <select
            id={`flag-reason-${standardCode}`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, marginBottom: 8 }}
          >
            {REASONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <label htmlFor={`flag-details-${standardCode}`} style={{ display: 'block', color: '#334155', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>Details (optional)</label>
          <textarea
            id={`flag-details-${standardCode}`}
            value={details}
            maxLength={1000}
            rows={3}
            onChange={(event) => setDetails(event.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, resize: 'vertical' }}
          />
          {error && <div role="alert" style={{ marginTop: 6, color: '#991b1b', fontSize: 11 }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <button type="button" onClick={close} disabled={saving} style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '6px 10px', border: 'none', borderRadius: 6, background: '#1e3a8a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: saving ? 'wait' : 'pointer' }}>
              {saving ? 'Sending…' : 'Send flag'}
            </button>
          </div>
        </form>
      )}
      {showThanks && !open && (
        <div role="status" style={{ position: 'absolute', right: 0, top: 36, zIndex: 10, whiteSpace: 'nowrap', padding: '4px 8px', background: '#ecfdf5', border: '1px solid #86efac', borderRadius: 6, color: '#166534', fontSize: 11, fontWeight: 700 }}>
          Thanks — we'll review it.
        </div>
      )}
    </div>
  );
}
